import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@clari/ui'

const STORAGE_KEY = 'clari-consent'

interface Consent {
  necessary: true
  analytics: boolean
  marketing: boolean
}

function load(): Consent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Consent) : null
  } catch {
    return null
  }
}

function save(consent: Consent): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
}

/** GDPR cookie consent — opt-in, granular, necessary-only by default. */
export function CookieConsent() {
  const { t } = useTranslation('common')
  const [visible, setVisible] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    if (!load()) setVisible(true)
  }, [])

  if (!visible) return null

  function persist(consent: Consent) {
    save(consent)
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-label={t('consent.title')}
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-xl rounded-2xl border border-border bg-bg-elevated p-5 shadow-lg"
    >
      <h2 className="text-base font-semibold">{t('consent.title')}</h2>
      <p className="mt-1 text-sm text-text-muted">{t('consent.body')}</p>

      <div className="mt-3 flex flex-col gap-2 text-sm">
        <label className="flex items-center gap-2 text-text-muted">
          <input type="checkbox" checked readOnly />
          {t('consent.necessary')}
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />
          {t('consent.analytics')}
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
          {t('consent.marketing')}
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => persist({ necessary: true, analytics, marketing })}>
          {t('consent.accept_selected')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => persist({ necessary: true, analytics: true, marketing: true })}
        >
          {t('consent.accept_all')}
        </Button>
        <Button
          variant="tertiary"
          onClick={() => persist({ necessary: true, analytics: false, marketing: false })}
        >
          {t('consent.reject')}
        </Button>
      </div>
    </div>
  )
}
